import React, { useState } from "react";
import { Loader2, Star } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "src/components/ui/dialog";
import { Button } from "src/components/ui/button";
import { Label } from "src/components/ui/label";
import { Textarea } from "src/components/ui/textarea";
import { Input } from "src/components/ui/input";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "src/components/ui/select";
import { useToast } from "src/components/ui/toast";
import { cn } from "src/utils/cn";
import { completeSiteVisit } from "src/features/site_visits/api/siteVisitsApi";
import { OUTCOME_META } from "src/features/site_visits/constants";

function StarRating({ value, onChange }) {
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((n) => (
        <Button key={n} type="button" variant="ghost" size="icon" onClick={() => onChange(n)} className="h-auto w-auto p-0.5 hover:bg-transparent">
          <Star
            className={cn(
              "h-6 w-6 transition-colors",
              n <= value ? "fill-amber-400 text-amber-400" : "text-slate-300"
            )}
          />
        </Button>
      ))}
    </div>
  );
}

// Captures the outcome + feedback when a checked-in visit is completed.
export function FeedbackDialog({ open, onOpenChange, visit, onCompleted }) {
  const { toast } = useToast();
  const [outcome, setOutcome] = useState("interested");
  const [rating, setRating] = useState(0);
  const [notes, setNotes] = useState("");
  const [budget, setBudget] = useState("");
  const [saving, setSaving] = useState(false);

  async function submit() {
    setSaving(true);
    try {
      const res = await completeSiteVisit(visit.id, {
        outcome,
        rating: rating || undefined,
        feedbackNotes: notes || undefined,
        budgetFeedback: budget || undefined,
      });
      toast({
        title: "Visit completed",
        description: res.suggest_deal ? "Outcome booked — you can now create a deal." : "Feedback recorded.",
      });
      onCompleted?.(res);
      onOpenChange(false);
    } catch (error) {
      const errs = error.response?.data?.errors;
      toast({
        variant: "destructive",
        title: "Couldn't save",
        description: Array.isArray(errs) ? errs.join(", ") : "Something went wrong.",
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Visit feedback</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Outcome</Label>
            <Select value={outcome} onValueChange={setOutcome}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(OUTCOME_META).map(([key, meta]) => (
                  <SelectItem key={key} value={key}>
                    {meta.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label>Buyer interest</Label>
            <StarRating value={rating} onChange={setRating} />
          </div>

          <div className="space-y-1.5">
            <Label>Budget feedback</Label>
            <Input
              placeholder="e.g. comfortable up to ₹1.2 Cr"
              value={budget}
              onChange={(e) => setBudget(e.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <Label>Notes</Label>
            <Textarea
              rows={3}
              placeholder="What did the prospect think?"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={saving}>
            {saving && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
            Complete visit
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
